import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { getFacturasEmitidas, getFacturasRecibidas } from "../../utils/conciliacionSII";
import { esNotaCredito, signoNC, tipoDteLabel } from "../../utils/dte";
import { calcularPeriodosIVA, detectarEstadoPago } from "../../utils/iva";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import { Header } from "../Header";
import styles from "./DashboardIva.module.scss";

// Régimen tributario chileno con sus tasas de PPM. El contador valida cuál usar.
// - General: 2% (default).
// - Pro Pyme General: 0.25% primeros 3 años, 0.5% después.
// - Pro Pyme Transparente: 0.25% primeros 3 años.
// Por simplicidad inicial usamos la tasa fija de "primeros 3 años" para los Pro Pyme.
const REGIMENES = [
  { value: "general", label: "Régimen General (2%)", tasa: 0.02 },
  { value: "propyme_general", label: "Pro Pyme General (0,25%)", tasa: 0.0025 },
  { value: "propyme_transparente", label: "Pro Pyme Transparente (0,25%)", tasa: 0.0025 },
];

const ESTADO_STYLE = {
  pagado: { background: "#e8f5e9", color: "#1b5e20", border: "#a5d6a7", label: "Pagado" },
  por_pagar: { background: "#fff8e1", color: "#7c5e00", border: "#ffe082", label: "Por pagar" },
  vencido: { background: "#ffebee", color: "#b71c1c", border: "#ef9a9a", label: "Vencido" },
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [, m, d] = f.split("-");
  return `${d}/${m}`;
};

const DashboardIva = () => {
  const { pais } = useAuthContext();
  const { formatAmount } = useCurrency();

  const [emitidas, setEmitidas] = useState([]);
  const [recibidas, setRecibidas] = useState([]);
  const [movimientosCaja, setMovimientosCaja] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regimen, setRegimen] = useState("general");
  const [modalPeriodo, setModalPeriodo] = useState(null);

  useEffect(() => {
    if (!pais || pais !== "Chile") return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getFacturasEmitidas(pais),
      getFacturasRecibidas(pais),
      client
        .from("movimientos_caja")
        .select("id, fecha, monto, descripcion, categoria_pago, estado")
        .eq("pais", pais)
        .eq("categoria_pago", "pago_impuesto")
        .neq("estado", "anulado"),
    ])
      .then(([em, rec, movsRes]) => {
        if (cancelled) return;
        setEmitidas(em || []);
        setRecibidas(rec || []);
        setMovimientosCaja(movsRes.data || []);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Error cargando datos del dashboard");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pais]);

  const tasaActual = useMemo(() => REGIMENES.find((r) => r.value === regimen)?.tasa ?? 0.02, [regimen]);

  const periodos = useMemo(
    () =>
      calcularPeriodosIVA(emitidas, recibidas, {
        tasaPPM: tasaActual,
        mesesAtras: 6,
        mesesAdelante: 3,
      })
        .filter((p) => p.periodo >= "2026-01")
        .map((p) => ({ ...p, ...detectarEstadoPago(p.periodo, p.fechaPago, movimientosCaja) })),
    [emitidas, recibidas, movimientosCaja, tasaActual]
  );

  // Totales del año en curso (acumulado)
  const totales = useMemo(() => {
    const año = new Date().getFullYear();
    const enAño = periodos.filter((p) => p.periodo.startsWith(String(año)));
    return {
      ivaDebito: enAño.reduce((s, p) => s + p.ivaDebito, 0),
      ivaCredito: enAño.reduce((s, p) => s + p.ivaCredito, 0),
      ivaAPagar: enAño.reduce((s, p) => s + p.ivaAPagar, 0),
      ppm: enAño.reduce((s, p) => s + p.ppm, 0),
      totalAPagar: enAño.reduce((s, p) => s + p.totalAPagar, 0),
      pagado: enAño.filter((p) => p.estado === "pagado").reduce((s, p) => s + (p.montoPago || 0), 0),
    };
  }, [periodos]);

  if (pais !== "Chile") {
    return (
      <div className={styles.wrapper}>
        <Header title="Dashboard IVA" />
        <div className={styles.empty}>El Dashboard IVA aplica solo para Chile. Módulo SUNAT pendiente para Perú.</div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Header title="Dashboard IVA">
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <label style={{ fontSize: "0.82rem", color: "#666", margin: 0 }}>Régimen:</label>
          <select
            className="form-select form-select-sm"
            value={regimen}
            onChange={(e) => setRegimen(e.target.value)}
            style={{ width: 240, fontSize: "0.82rem" }}
          >
            {REGIMENES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </Header>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando…</div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: 12,
                marginBottom: 20,
              }}
            >
              {[
                { label: "IVA débito (año)", value: totales.ivaDebito, color: "#2e7d32" },
                { label: "IVA crédito (año)", value: totales.ivaCredito, color: "#c62828" },
                { label: "IVA a pagar (año)", value: totales.ivaAPagar, color: "#1a3a5c" },
                { label: "PPM (año)", value: totales.ppm, color: "#1a3a5c" },
                { label: "Total a pagar (año)", value: totales.totalAPagar, color: "#1a3a5c" },
                { label: "Ya pagado (año)", value: totales.pagado, color: "#2e7d32" },
              ].map((c) => (
                <div
                  key={c.label}
                  style={{
                    background: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: 6,
                    padding: "10px 12px",
                  }}
                >
                  <div style={{ fontSize: "0.72rem", color: "#666", marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontSize: "1.1rem", fontWeight: 700, color: c.color }}>
                    {formatAmount(c.value, "CLP")}
                  </div>
                </div>
              ))}
            </div>

            <table className="table table-sm table-hover" style={{ fontSize: "0.85rem" }}>
              <thead className="table-dark">
                <tr>
                  <th className="text-start">Período</th>
                  <th className="text-end">IVA débito</th>
                  <th className="text-end">IVA crédito</th>
                  <th className="text-end">IVA neto</th>
                  <th className="text-end">Ventas afectas</th>
                  <th className="text-end">PPM</th>
                  <th className="text-end">Total a pagar</th>
                  <th className="text-start">Vence</th>
                  <th className="text-start">Estado</th>
                </tr>
              </thead>
              <tbody>
                {periodos.map((p) => {
                  const style = ESTADO_STYLE[p.estado] || ESTADO_STYLE.por_pagar;
                  return (
                    <tr
                      key={p.periodo}
                      style={{ cursor: p.estimado ? "default" : "pointer" }}
                      onClick={() => {
                        if (!p.estimado) setModalPeriodo(p);
                      }}
                      title={p.estimado ? "" : "Ver desglose de DTEs"}
                    >
                      <td className="text-start" style={{ fontWeight: 600 }}>
                        {p.mesLabel.split(" ")[0]}
                        {p.estimado && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: "0.7rem",
                              background: "#f39c12",
                              color: "#fff",
                              borderRadius: 3,
                              padding: "1px 5px",
                            }}
                          >
                            Estimado
                          </span>
                        )}
                      </td>
                      <td className="text-end">{formatAmount(p.ivaDebito, "CLP")}</td>
                      <td className="text-end">{formatAmount(p.ivaCredito, "CLP")}</td>
                      <td
                        className="text-end"
                        style={{ color: p.ivaNeto < 0 ? "#2e7d32" : "#1a3a5c", fontWeight: 600 }}
                      >
                        {p.ivaNeto < 0
                          ? `+${formatAmount(p.ivaAFavor, "CLP")} a favor`
                          : formatAmount(p.ivaNeto, "CLP")}
                      </td>
                      <td className="text-end">{formatAmount(p.ventasNetas, "CLP")}</td>
                      <td className="text-end">{formatAmount(p.ppm, "CLP")}</td>
                      <td className="text-end" style={{ fontWeight: 700 }}>
                        {formatAmount(p.totalAPagar, "CLP")}
                      </td>
                      <td className="text-start">{formatFecha(p.fechaPago)}</td>
                      <td className="text-start">
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 4,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            background: style.background,
                            color: style.color,
                            border: `1px solid ${style.border}`,
                          }}
                        >
                          {style.label}
                        </span>
                        {p.estado === "pagado" && p.fechaPago && (
                          <span style={{ marginLeft: 6, fontSize: "0.7rem", color: "#666" }}>
                            {formatFecha(p.fechaPago)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 12 }}>
              IVA neto se calcula como débito − crédito. Notas de crédito (DTE 61) restan a sus respectivos lados. PPM
              aplica solo sobre ventas afectas (DTE 33 + 56). Pagados se detectan vía
              <code style={{ margin: "0 4px" }}>movimientos_caja</code>
              con <code>categoria_pago=&apos;pago_impuesto&apos;</code> y descripción que contenga el período. Hacé
              click en una fila para ver el desglose de DTEs.
            </div>
          </>
        )}
      </div>

      {modalPeriodo && (
        <PeriodoDetalleModal
          periodo={modalPeriodo}
          emitidas={emitidas.filter((f) => f.periodo_tributario === modalPeriodo.periodo)}
          recibidas={recibidas.filter((f) => f.periodo_tributario === modalPeriodo.periodo)}
          formatAmount={formatAmount}
          onClose={() => setModalPeriodo(null)}
        />
      )}
    </div>
  );
};

const PeriodoDetalleModal = ({ periodo, emitidas, recibidas, formatAmount, onClose }) => {
  const [tab, setTab] = useState("emitidas");
  const list = tab === "emitidas" ? emitidas : recibidas;

  const totalIvaEmitidas = emitidas.reduce((s, f) => s + signoNC(f) * Number(f.monto_iva || 0), 0);
  const totalIvaRecibidas = recibidas.reduce((s, f) => s + signoNC(f) * Number(f.monto_iva || 0), 0);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 8,
          width: "min(960px, 95vw)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid #dee2e6",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: "1rem", fontWeight: 700, color: "#1a3a5c" }}>{periodo.mesLabel}</div>
            <div style={{ fontSize: "0.75rem", color: "#666" }}>
              {emitidas.length} emitidas · {recibidas.length} recibidas
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: "#666",
              padding: "0 8px",
              lineHeight: 1,
            }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div style={{ display: "flex", borderBottom: "1px solid #dee2e6" }}>
          {[
            { value: "emitidas", label: `Emitidas (${emitidas.length})` },
            { value: "recibidas", label: `Recibidas (${recibidas.length})` },
          ].map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              style={{
                flex: 1,
                padding: "10px 16px",
                border: "none",
                background: tab === t.value ? "#1a3a5c" : "#f8f9fa",
                color: tab === t.value ? "#fff" : "#666",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: "pointer",
                borderRight: t.value === "emitidas" ? "1px solid #dee2e6" : "none",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ overflow: "auto", flex: 1 }}>
          {list.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#999", fontSize: "0.85rem" }}>
              Sin DTEs en este período.
            </div>
          ) : (
            <table className="table table-sm table-hover" style={{ fontSize: "0.8rem", marginBottom: 0 }}>
              <thead className="table-dark" style={{ position: "sticky", top: 0 }}>
                <tr>
                  <th className="text-start">Folio</th>
                  <th className="text-start">Razón social</th>
                  <th className="text-start">Tipo DTE</th>
                  <th className="text-end">Neto</th>
                  <th className="text-end">IVA</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {list.map((f) => {
                  const esNC = esNotaCredito(f);
                  const razon =
                    tab === "emitidas"
                      ? f.razon_social_receptor || f.rut_receptor || "—"
                      : f.razon_social_emisor || f.rut_emisor || "—";
                  const neto =
                    Number(f.monto_neto) === 0 && Number(f.monto_exento) > 0
                      ? Number(f.monto_exento)
                      : Number(f.monto_neto || 0);
                  const signo = esNC ? -1 : 1;
                  return (
                    <tr key={f.id} style={esNC ? { background: "#fff5f5", color: "#b71c1c" } : null}>
                      <td>{f.folio || "—"}</td>
                      <td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>{razon}</td>
                      <td>{tipoDteLabel(f.tipo_dte)}</td>
                      <td className="text-end">{formatAmount(signo * neto, "CLP")}</td>
                      <td className="text-end">{formatAmount(signo * Number(f.monto_iva || 0), "CLP")}</td>
                      <td className="text-end" style={{ fontWeight: 600 }}>
                        {formatAmount(signo * Number(f.monto_total || 0), "CLP")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid #dee2e6",
            background: "#f8f9fa",
            display: "flex",
            justifyContent: "space-around",
            fontSize: "0.85rem",
          }}
        >
          <div>
            <span style={{ color: "#666", marginRight: 8 }}>IVA débito (emitidas):</span>
            <strong style={{ color: "#2e7d32" }}>{formatAmount(totalIvaEmitidas, "CLP")}</strong>
          </div>
          <div>
            <span style={{ color: "#666", marginRight: 8 }}>IVA crédito (recibidas):</span>
            <strong style={{ color: "#c62828" }}>{formatAmount(totalIvaRecibidas, "CLP")}</strong>
          </div>
          <div>
            <span style={{ color: "#666", marginRight: 8 }}>IVA neto:</span>
            <strong style={{ color: "#1a3a5c" }}>{formatAmount(totalIvaEmitidas - totalIvaRecibidas, "CLP")}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardIva;
